import classNames from 'classnames';
import type { ReactNode } from 'react';

import type { Sort } from '@/utils/use-sort';

import { NotYetInfo, notYet, type NotYetKey } from './not-yet';
import { table } from './styles';

/**
 * A column header that sorts its table: a click sorts by the column, a second click
 * reverses it. The sorted column reads darker, with its arrow; the others show a
 * faint pair, so it is clear which columns sort. Only columns where an order means
 * something get one.
 */
export const SortTh = ({
  label,
  column,
  sort,
  onSort,
  right,
  className,
  notYet: off,
}: {
  label: ReactNode;
  column: string;
  sort: Sort | null;
  onSort: (column: string) => void;
  /** A right-aligned column (numbers): the arrow goes before the label. */
  right?: boolean;
  className?: string;
  /** The column does not work in this version: its header shows faded, with the info mark. */
  notYet?: NotYetKey;
}) => {
  const on = sort?.key === column;
  const button = (
    <button
      type="button"
      onClick={() => onSort(column)}
      className={classNames(
        // 44px to tap on a phone (UI-1), grown into the cell's padding so the row keeps its height.
        'relative -my-3.5 rounded py-3.5 transition-colors hover:text-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/50 md:my-0 md:py-0',
        right ? 'text-right' : 'text-left',
        on ? 'text-fg-strong' : 'text-fg-muted',
        off && notYet,
      )}
    >
      {label}
      {/* In the cell's padding (16px: the arrow and its gap), so sorting never widens a column. */}
      <svg
        viewBox="0 0 16 16"
        className={classNames('absolute top-1/2 h-3 w-3 -translate-y-1/2', right ? 'right-full mr-1' : 'left-full ml-1', !on && 'opacity-40')}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d={!on ? 'M5 6.5L8 3.5l3 3M5 9.5l3 3 3-3' : sort.desc ? 'M4.5 6.5L8 10l3.5-3.5' : 'M4.5 9.5L8 6l3.5 3.5'} />
      </svg>
    </button>
  );
  return (
    <th
      scope="col"
      aria-sort={on ? (sort.desc ? 'descending' : 'ascending') : undefined}
      className={classNames(table.th, right && 'text-right', className)}
    >
      {off ? (
        // The info mark after the label: in the cell's padding for a right-aligned column, the way
        // its arrow sits in the padding on the left, so the mark never widens the column; past the
        // arrow for a left-aligned one.
        <span className={classNames('relative', right ? 'inline-block' : 'inline-flex items-center gap-5')}>
          {button}
          <NotYetInfo what={off} small className={right ? 'absolute left-full top-1/2 ml-0.5 -translate-y-1/2' : undefined} />
        </span>
      ) : (
        button
      )}
    </th>
  );
};

export default SortTh;
